import CloseIcon from '@mui/icons-material/Close';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import {
  Box,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import debounce from 'lodash/debounce';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import TablePaginationActions from '@cashier/components/TablePaginationActions';
import { CSVLink as ReactCSVLink } from 'react-csv';
import Highlighter from 'react-highlight-words';

import { PAGINATION_SIZE } from '@constants/index';
import { Dimmer } from '@shared/components';
import EmptyMessage from '@shared/components/EmptyMessage';
import Search from '@shared/components/Search';

import { getInputPattern } from '@shared/utils/inputValidation';
import { columnSearchErrorMessages, getComparator, stableSort } from './tableSortUtils';
import './TableView.scss';
import {
  escapeRegExpForHighlight,
  formatCellValueForSearch,
  getHeaderColumnReactKey,
  getTableRowReactKey,
  renderHeaderLabelContent,
} from './tableViewHelpers';

import type {
  ColumnSearch,
  CSVLinkProps,
  ErrorField,
  HighlighterProps,
  OrderBy,
  OrderDirection,
  SearchData,
  TableViewProps,
} from './types';

const CSVLink = ReactCSVLink as React.ComponentType<CSVLinkProps>;
const HighlighterComponent: React.FC<HighlighterProps> = Highlighter as any;

const useStyles = makeStyles(() => ({
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
}));

const TableView: React.FC<TableViewProps> = ({
  rowProps,
  onSearch,
  pagination,
  headers = [],
  rows = [],
  mapRowData = (row: any) => row,
  csvExport = false,
  columnsSearch = [],
  columnSearchDelay = 500,
  isLoading = false,
  onSortingChange = () => { },
  sort,
}) => {
  const classes = useStyles();
  const [orderBy, setOrderBy] = useState<OrderBy>(() =>
    sort?.property && sort?.order ? { [sort.property]: sort.order as OrderDirection } : {}
  );
  const [searchData, setSearchData] = useState<SearchData>({});
  const [rowData, setRowData] = useState<any[] | null>(null);
  const [search, setSearch] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [columnName, setColumnName] = useState<string>('');
  const [tempValues, setTempValues] = useState<SearchData>({});
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(PAGINATION_SIZE);

  const debouncedSearch = useMemo(
    () =>
      debounce((name: string, value: string, column?: ColumnSearch) => {
        setSearchData((prev) => ({
          ...prev,
          [name]: value,
        }));
        onSearchColumn(name, value, column);
      }, columnSearchDelay),
    [columnSearchDelay]
  );

  const handleInputChange = (name: string, value: string, column?: ColumnSearch): void => {
    if (loading || isLoading) {
      return;
    }

    const inputPattern = getInputPattern(column?.type ?? 'default');
    if (value && !inputPattern.test(value)) {
      return;
    }

    setTempValues((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (columnSearchDelay === 0) {
      setSearchData((prev) => ({
        ...prev,
        [name]: value,
      }));
      onSearchColumn(name, value, column);
      return;
    }

    debouncedSearch(name, value, column);
  };

  const [error, setError] = useState<ErrorField[]>(
    columnsSearch.map((item) => ({
      name: item.name,
      error: false,
      helperText: '',
    }))
  );

  const filterRows = (value: string, rowList: any[], nameColumn?: string): void => {
    const searchValue = String(value).toUpperCase();

    let newRow = rowList?.map(mapRowData).filter((item) => {
      if (!nameColumn) {
        return true;
      }

      const cellValue = item[nameColumn];
      return formatCellValueForSearch(cellValue).includes(searchValue);
    });

    if (!pagination && Object.keys(orderBy).length > 0) {
      const property = Object.keys(orderBy)[0];
      const order = orderBy[property];
      newRow = stableSort(newRow, getComparator(order, property));
    }

    setRowData(newRow);
  };

  useEffect(() => {
    setRowsPerPage(rowsPerPage || PAGINATION_SIZE);
  }, []);

  useEffect(() => {
    filterRows(search, rows, columnName);
  }, [rows, orderBy]);

  useEffect(() => {
    if (pagination) {
      setPage(pagination?.pageNumber ?? 0);
    }
  }, [pagination?.pageNumber]);

  const createSortHandler = (property: string) => (): void => {
    const currentOrder = orderBy[property];
    let newOrderBy: OrderBy = {};

    if (currentOrder === 'asc') {
      newOrderBy[property] = 'desc';
    } else {
      newOrderBy[property] = 'asc';
    }

    setOrderBy(newOrderBy);
    onSortingChange({ property, order: newOrderBy[property] });
  };

  const onChangeSearch = (value: string = ''): void => {
    const searchValue = value.toUpperCase();
    setSearch(value);
    setLoading(true);
    filterRows(searchValue, rows, columnName);
    setLoading(false);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setPage(0);
    pagination?.handleChangeRowsPerPage?.(0, Number(event.target.value));
    setRowsPerPage(Number(event.target.value));
  };

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number): void => {
    setPage(newPage);
    pagination?.handleChangePage?.(newPage, rowsPerPage);
  };

  const onSearchColumn = (name: string, value: string = '', column?: ColumnSearch): void => {
    handleSetError(name);
    if (value === '+') {
      return;
    }
    const newData = { ...searchData };
    newData[name] = value;
    setSearchData(newData);

    onChangeSearch(value?.trim());
    setColumnName(name);
    column?.onSearch?.(name, value?.trim());
  };

  const handleSetError = (nameField: string, error: boolean = false, text: string = ''): void => {
    setError((prevFields) =>
      prevFields.map((field) => (field.name === nameField ? { ...field, error, helperText: text } : field))
    );
  };

  const handleClear = (name: string): void => {
    const el = inputRefs.current[name];
    if (el) {
      el.value = '';
    }
    setTempValues((prev) => ({
      ...prev,
      [name]: '',
    }));

    setSearchData((prev) => ({
      ...prev,
      [name]: '',
    }));
    onSearchColumn(name, '');
  };

  const handleSetErrorAt = (idx: number, name: string, isError: boolean, type?: string): void => {
    setError((prev) => {
      const next = [...prev];
      next[idx] = {
        name,
        error: isError,
        helperText: isError
          ? (columnSearchErrorMessages[type ?? ''] ?? columnSearchErrorMessages.DEFAULT)
          : '',
      };
      return next;
    });
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>, item: ColumnSearch, index: number) => {
    if (loading || isLoading) {
      return;
    }

    const currentValue = event.target.value;
    const newValue = currentValue.replace(/^\s+/, '');
    const inputPattern = getInputPattern(item?.type ?? 'default');
    if (newValue && !inputPattern.test(newValue)) {
      event.target.value = tempValues[item.name] ?? '';
      handleSetErrorAt(index, item.name, true, item?.type);
      return;
    }
    handleSetErrorAt(index, item.name, false);

    if (newValue !== searchData[item.name]) {
      handleInputChange(item.name, newValue, item);
    }
  };

  const getEndAdornment = (item: ColumnSearch) => {
    if (tempValues[item.name]) {
      return (
        <InputAdornment position="end">
          <IconButton
            onClick={() => {
              handleClear(item.name);
              onSearchColumn(item.name, '', item);
            }}
            onMouseDown={() => {
              handleClear(item.name);
              onSearchColumn(item.name, '', item);
            }}>
            <CloseIcon />
          </IconButton>
        </InputAdornment>
      );
    }
    return null;
  };

  const getSearchSection = (): React.ReactNode => {
    return (
      columnsSearch && (
        <div
          className={clsx('table-search-content', {
            'has-padding': columnsSearch.length > 0,
          })}>
          {columnsSearch.map((item, index) => {
            return (
              item.component || (
                <TextField
                  key={item.name}
                  variant="outlined"
                  label={item.label}
                  inputRef={(el) => (inputRefs.current[item.name] = el)}
                  value={tempValues[item.name] ?? ''}
                  onChange={(event) => onChange(event, item, index)}
                  slotProps={{
                    inputLabel: { shrink: true },
                    input: {
                      style: {
                        backgroundColor: 'white',
                        borderRadius: '12px',
                      },
                      endAdornment: getEndAdornment(item),
                    },
                  }}
                  error={error[index]?.error || false}
                  helperText={error[index]?.helperText ?? ''}
                />
              )
            );
          })}
        </div>
      )
    );
  };

  return (
    <div className="table-view-content">
      <Dimmer active={loading || isLoading}>
        {getSearchSection()}
        <TableContainer component={Box} style={{ borderRadius: '8px', background: 'white', borderColor: 'unset' }}>
          <Table aria-label="simple table">
            <TableHead>
              {(onSearch || csvExport) && (
                <TableRow>
                  <TableCell colSpan={headers.length} align="center">
                    {onSearch && <Search onChangeSearch={onChangeSearch} />}
                    {csvExport && (
                      <span>
                        <CSVLink
                          filename={`${new Date().toLocaleDateString()}.csv`}
                          data={rowData || []}
                          headers={headers
                            ?.filter((item) => item.csv)
                            .map((item) => ({
                              label: String(item.label),
                              key: item.id,
                            }))}
                          uFEFF>
                          <IconButton>
                            <SaveAltIcon />
                          </IconButton>
                        </CSVLink>
                        CSV
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                {headers?.map((item, index) => {
                  const { label, renderHeaderCell, HeaderCellProps, id, isSortable } = item;
                  const align = (item as any).align || 'left';
                  const headerKey = getHeaderColumnReactKey(item, index);

                  if (renderHeaderCell) {
                    return renderHeaderCell(item);
                  }
                  return (
                    <TableCell className="table-view-header" align={align} {...HeaderCellProps} key={headerKey}>
                      {isSortable ? (
                        <TableSortLabel active={!!orderBy[id]} direction={orderBy[id]} onClick={createSortHandler(id)}>
                          {renderHeaderLabelContent(label)}
                          {orderBy[id] ? (
                            <span className={classes.visuallyHidden}>
                              {orderBy[id] === 'desc' ? 'sorted descending' : 'sorted ascending'}
                            </span>
                          ) : null}
                        </TableSortLabel>
                      ) : (
                        renderHeaderLabelContent(label)
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {!rowData?.length && (
                <TableRow>
                  <TableCell colSpan={headers.length} align="center" sx={{ borderBottom: 'unset' }}>
                    <EmptyMessage />
                  </TableCell>
                </TableRow>
              )}
              {!!rowData?.length &&
                rowData?.map((row, rowIndex) => {
                  const rowRecord = row as Record<string, unknown>;
                  return (
                    <TableRow key={getTableRowReactKey(rowRecord, rowIndex)} hover {...rowProps}>
                      {headers.map((header, colIndex) => {
                        const { renderRowCell, id: columnId, rowCellProps, isNumber } = header;
                        const cellKey = getHeaderColumnReactKey(header, colIndex);
                        if (renderRowCell) {
                          return (
                            <TableCell
                              className="table-view-cell"
                              align={isNumber ? 'right' : 'left'}
                              {...rowCellProps}
                              key={cellKey}
                              sx={{
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                verticalAlign: 'middle',
                                overflow: 'visible',
                                padding: '8px',
                              }}>
                              {renderRowCell(row[columnId], row, header, colIndex)}
                            </TableCell>
                          );
                        }
                        return (
                          <TableCell
                            className="table-view-cell"
                            align={isNumber ? 'right' : 'left'}
                            {...rowCellProps}
                            key={cellKey}>
                            {search &&
                              typeof (String(row[columnId]) === 'string') &&
                              String(columnId) === String(columnName) ? (
                              <HighlighterComponent
                                searchWords={[escapeRegExpForHighlight(search)]}
                                textToHighlight={String(row[columnId])?.replace(/\s+/g, ' ')}
                                autoEscape={false}
                              />
                            ) : (
                              row[columnId]
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
            </TableBody>
            {pagination && !!rowData?.length && (
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    count={pagination.rowsTotalCount ?? 0}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    ActionsComponent={TablePaginationActions}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{ borderBottom: 'unset' }}
                  />
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Dimmer>
    </div>
  );
};

export default TableView;
